import { ExerciseStat, FitnessState } from '@cf-shared/data-model/user-state';
import { FitnessPlan, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { IntakeFormState } from '@cf-shared/data-model/intake-form-state';
import { parseJSONSafely } from '@cf-shared/shared-util';
import { defaultCustomWeightIncrement_kg, isPlausibleEquipmentStep_kg } from '../util/custom-weight-increment';

// Settings shape used by migrations — kept to only the fields actually read/written so that
// this file doesn't depend on user-db.service.ts (which drags in PouchDB, unavailable under node).
export interface MigratableSettings {
  unitsPrefsJSON: string;
}

// legacy shape — top-level weight_unit used to live on FitnessState. it was removed in the #3099
// follow-up; exported so migration code and its tests can read/delete the field without `any`.
export type LegacyFitnessState = FitnessState & { weight_unit?: string };

// migrate legacy FitnessState.weight_unit. for existing users this may be the only persisted trace
// of the bodyweight unit they picked during intake, so back-port it into settings.unitsPrefsJSON
// (as 'bodyweight_kg') before stripping the field. idempotent: if prefs already carries the key,
// the legacy value is discarded. returns true when either doc was mutated — the caller must persist
// both docs, otherwise the cleanup stays in-memory and the legacy field keeps re-appearing on sync.
export function migrateLegacyFitnessWeightUnit(doc: FitnessState, settings: MigratableSettings): boolean {
  const legacyDoc = doc as LegacyFitnessState;
  if (!('weight_unit' in legacyDoc)) return false;
  const legacyUnit = legacyDoc.weight_unit;
  if (legacyUnit === 'kg' || legacyUnit === 'lb') {
    // harden against a corrupt unitsPrefsJSON that decodes to something other than a plain object
    // (array, primitive, null) — a direct property assignment would either throw or silently
    // drop the key when we stringify. log when discarding so debugging a stuck migration has a
    // trace of what was there before.
    let prefs = parseJSONSafely<Record<string, string>>(settings.unitsPrefsJSON);
    if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) {
      if (settings.unitsPrefsJSON) {
        console.log('migrateLegacyFitnessWeightUnit: discarding corrupt unitsPrefsJSON', settings.unitsPrefsJSON);
      }
      prefs = {};
    }
    if (!prefs['bodyweight_kg']) {
      prefs['bodyweight_kg'] = legacyUnit;
      settings.unitsPrefsJSON = JSON.stringify(prefs);
    }
  }
  delete legacyDoc.weight_unit;
  return true;
}

/**
 * Phase 2 migration: lift `Plan_ExercisePath.originalTargetReps` onto
 * `ExerciseStat.preferRepTargetByIntensity`. Pre-Phase-2 the field conflated two concerns —
 * user-stated rep target preference AND a `targetHit` auto-record of last achieved reps — but
 * BOTH serve as the user's progression anchor (Case-1 cap-restore, Case-2 +1 bump). Phase 2
 * preserves the auto-record semantics in the new location: every defined `originalTargetReps`
 * is a meaningful anchor and is lifted, regardless of whether it matches the system default.
 *
 * **Idempotency.** A pre-existing `preferRepTargetByIntensity[level]` is never stomped — only the
 * `delete path.originalTargetReps` runs. Re-runs after a partial flush converge correctly.
 *
 * **Containers walked.** `plan.subSlots[*]` (active plan) and `plan.generatedPlanBackup?.subSlots[*]`
 * (restore-from-backup target). NOT walked: `fitnessState.oldSubslots` (already wiped on init at
 * `user-db.service.ts:413`, lifting from an about-to-be-wiped source has no production value);
 * `LogWorkout.exercises[*].planSubslotBackup` (unbounded historical records, read-only/display-only,
 * post-Phase-2B field-undefined is acceptable display behavior).
 *
 * Returns `true` if any document was mutated. Caller must persist BOTH `plan` (path deletes) and
 * `fitnessState` (lifted preferences) — see `flushPendingMigrationSavesAsync`.
 */
export function migrateOriginalTargetRepsToExerciseStat(
  plan: FitnessPlan,
  fitnessState: FitnessState,
): boolean {
  let mutated = false;

  const visitSubSlots = (subSlots: { [id: string]: Plan_SubSlot } | undefined) => {
    if (!subSlots) return;
    for (const subSlotId of Object.keys(subSlots)) {
      const subSlot = subSlots[subSlotId];
      if (!subSlot || !subSlot.exercisePaths) continue;
      for (const path of subSlot.exercisePaths) {
        if (path.originalTargetReps === undefined) continue;
        // lift the value onto ExerciseStat. lazy-init the stat record (codebase precedent at
        // exercise-configuration-page.ts:handleMyoChange uses the same `|| {} as ExerciseStat`
        // pattern; weight_unit / estimated1RM_timestamp are populated by other code paths on first
        // use, e.g. workout-detail.ts seeds weight_unit from logSet.stats on first set log).
        const exerciseId = subSlot.exercise_id;
        let stat = fitnessState.exerciseStats[exerciseId];
        if (!stat) {
          stat = {} as ExerciseStat;
          fitnessState.exerciseStats[exerciseId] = stat;
        }
        stat.preferRepTargetByIntensity ||= {};
        // idempotency: never stomp a more-recent user preference. We still run the delete below
        // so a partial-flush retry converges (the lift-skip + delete combination is the convergence
        // mechanism documented in design-docs/exercise-replacement.md).
        if (stat.preferRepTargetByIntensity[path.intensityLevel] === undefined) {
          stat.preferRepTargetByIntensity[path.intensityLevel] = path.originalTargetReps;
        }
        delete path.originalTargetReps;
        mutated = true;
      }
    }
  };

  visitSubSlots(plan.subSlots);
  visitSubSlots(plan.generatedPlanBackup?.subSlots);

  return mutated;
}

/**
 * Recover fitnessState.weight_kg for users whose data was corrupted by:
 *   - Bug A (pre-#3286 fix): edit-weigh-in.saveAsync wiped the field when
 *     a Today weigh-in entry was saved without a weight value (tape-only,
 *     caliper-only, or just open-and-save).
 *   - Bug B (pre-#3286 fix): coach slot fill never propagated the user's
 *     weight from intakeFormState into slot.fitnessState; the empty value
 *     was then written into the client's fitnessState on claim.
 *
 * The repaired field unblocks plan recalculation (`analyzeWeightIns` no
 * longer throws "Unable to find any bodyweight."). Tries, in order:
 *   1. `intakeFormState.weight_kg` — set from intake input, survives Bug A
 *      and is written by Bug B's coach intake before fix.
 *   2. `fitnessPlan.startPoint?.BW_kg` — captured when the plan was
 *      generated; fine for users whose fitnessState was wiped after intake.
 *
 * Returns true if any source produced a finite positive value.
 */
export function recoverMissingFitnessWeight(
  fitnessState: FitnessState,
  intakeFormState: IntakeFormState | undefined,
  fitnessPlan: FitnessPlan | undefined,
): boolean {
  // if current value is already a valid positive number, leave it alone.
  // anything else (undefined, null, NaN, 0, negative) is corrupted state we
  // should try to repair from the recovery sources below.
  if (typeof fitnessState.weight_kg === 'number' && isFinite(fitnessState.weight_kg) && fitnessState.weight_kg > 0) {
    return false;
  }
  const candidates: Array<number | undefined> = [
    intakeFormState?.weight_kg,
    fitnessPlan?.startPoint?.BW_kg,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && isFinite(candidate) && candidate > 0) {
      fitnessState.weight_kg = candidate;
      return true;
    }
  }
  return false;
}

/** One step size `repairImplausibleCustomWeightIncrements` replaced, for the caller to report. */
export interface RepairedCustomWeightIncrement {
  exerciseId: string;
  unit: 'kg' | 'lb';
  storedIncrement_kg: number;
  repairedIncrement_kg: number;
}

/**
 * #5344: repair equipment step sizes already stored on the account that describe no real machine.
 *
 * The background, in plain words. When somebody logs a weight the machine assigned to an exercise
 * cannot produce, the app un-assigns that machine and invents a temporary one, guessing its step
 * size from the difference between the weight just entered and the weight from the session before.
 * That guess is kept in `ExerciseStat.customWeightIncrementByUnit`, and before #5325 the leftover of
 * entering a round 61 kg against a 61.23496995 kg stack position could become a step of
 * 0.23496995 kg. #5325 stopped new ones being written; this repairs those already stored, so nobody
 * has to re-pick their equipment by hand. Full rules: `design-docs/reactive-deload.md`, #4337.
 *
 * Three things about HOW it repairs, each of which would be a bug if done the other way:
 *
 * - It REPLACES, never deletes the key. `UiExercise.checkEquipment` builds the temporary equipment
 *   only when a value is recorded, and the original un-assign already cleared the real equipment
 *   ids, so a deleted key sends the user into a lookup that cannot find anything.
 * - It prefers the OTHER unit's step when that one is genuine, and only falls back to the shared
 *   default otherwise. `resolveCustomWeightIncrementSource` rescues a corrupt unit by reading across
 *   to the other one; writing a default over the corrupt value would make it plausible, so that
 *   rescue would never fire again and a real stack step would be lost for good.
 * - It judges roundness only. The predicate's other rule needs the weight the step has to load,
 *   which nothing can establish from stored state at boot; `checkEquipment` applies that half at
 *   read time, where the last logged weight is at hand.
 *
 * Idempotent — every value it writes is itself plausible, so a second run changes nothing. Returns
 * one entry per replacement, so the caller can persist `fitnessState` and report what it found.
 *
 * A standing normaliser, NOT a one-shot keyed to a release: an app build older than v1.33.0 keeps
 * writing bad values for as long as it stays installed on somebody's phone.
 */
export function repairImplausibleCustomWeightIncrements(fitnessState: FitnessState): RepairedCustomWeightIncrement[] {
  const repaired: RepairedCustomWeightIncrement[] = [];
  if (!fitnessState.exerciseStats) return repaired;
  for (const exerciseId in fitnessState.exerciseStats) {
    const stat: ExerciseStat = fitnessState.exerciseStats[exerciseId];
    const incrementByUnit = stat?.customWeightIncrementByUnit;
    if (!incrementByUnit) continue;
    // read both units BEFORE repairing either, so "does the other unit hold a genuine step" is
    // asked of what the user's account really carried. Judging it against the live object instead
    // would let a default this very loop invented for the first unit pass as the second unit's
    // real machine, and both units would end up on the kg default.
    const storedByUnit = { kg: incrementByUnit.kg, lb: incrementByUnit.lb };
    for (const unit of ['kg', 'lb'] as const) {
      const storedIncrement_kg = storedByUnit[unit];
      // never recorded for this unit — nothing to repair, and writing one would invent a temporary
      // equipment for a unit the user has not unbrowsed
      if (storedIncrement_kg === undefined) continue;
      if (isPlausibleEquipmentStep_kg(storedIncrement_kg)) continue;
      // if the other unit holds a genuine step, that is this user's real machine and it beats an
      // invented default. Both keys store kilograms, so it carries across with no conversion.
      const otherUnitIncrement_kg = storedByUnit[unit === 'kg' ? 'lb' : 'kg'];
      const repairedIncrement_kg = isPlausibleEquipmentStep_kg(otherUnitIncrement_kg)
        ? otherUnitIncrement_kg
        : defaultCustomWeightIncrement_kg(unit);
      incrementByUnit[unit] = repairedIncrement_kg;
      repaired.push({ exerciseId, unit, storedIncrement_kg, repairedIncrement_kg });
    }
  }
  return repaired;
}
